import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import styles from './AggregateConflict.module.css';
import {
  API_RESOURCE_URLS,
  RESOURCE_TYPE,
  DATE_FORMAT,
  USER_OPERATIONS,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
} from '../../constants';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { withAllowedOperationsProvider } from '../../hocs';
import {
  buildErrorMessage,
} from '../../apis/calls';
import { BackButton, AggregateConflictToolbar } from '..';
import AggregateConflictTable from '../../components/AggregateConflictTable/AggregateConflictTable';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { API } from '../../apis/api';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { downloadCsv } from '../../utils/utils';
import { PANTNAGAR_PLANT_NAME } from '../../constants';

const AggregateConflict = ({
  aggregate: materialGroupCluster,
  hasActiveEdits,
  loadAggregateConflicts,
  setHasActiveEdits,
  lastRefreshedDate,
  areColumnsEditable,
  setEditableColumns,
  setConflictCount,
  rowsData,
  setRowsData,
  startDate,
  activeAsnDate,
  setActiveAsnDate,
}) => {
  const history = useHistory();
  const ROW_HEIGHT = 50;

  const {
    showPopup,
    showInternalError,
    showLoading,
    closePopUp,
  } = usePopupManager();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;
  const [lineColumns, setLineColumns] = useState([]);
  const [updatedValue, setEndEditing] = useState({
    isDataEdited: false,
    updatedData: [],
  });
  const [isRenderEditQty, setIsRenderEditQty] = useState(false);
  const [isDataValid, setValidData] = useState(false);

  const aggregateColumns = [
    {
      width: 150,
      title: 'Part number',
      field: 'childMaterialNumber',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 175,
      title: 'Part Description',
      field: 'childMaterialDescription',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: 'Chassis Line',
      field: 'parentLineName',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 160,
      title: 'Total Quantity',
      field: 'totalProducedChildMaterialsQuantity',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: 'Conflicted Quantity',
      field: 'unAllocatedQty',
      enableSearch: true,
      enableFilter: true,
      render: row => (
        <div
          className={
            row.unAllocatedQty === 0 ? styles.conflictResolved : styles.boldText
          }
        >
          {row.unAllocatedQty}
        </div>
      ),
    },
  ];

  const optionalColumns = [
    {
      width: 170,
      title: 'VC Material',
      field: 'rootMaterial',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: 'Vc Alias',
      field: 'rootMaterialAlias',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 300,
      title: 'VC Material Description',
      field: 'rootMaterialDescription',
      enableSearch: true,
      enableFilter: true,
    }
  ];


  const fetchAggregateConflictData = async allocatedChildProducedMaterials => {
    let maxLengthOfLineArray = 0;
    allocatedChildProducedMaterials.forEach((item, indexValue) => {
      let sumOfChildQty = 0;
      let lineArray = [];
      item.lineAndAllocatedProducedChildMaterialsQuantities.forEach(
        (childItem, index) => {
          sumOfChildQty += childItem.allocatedProducedMaterialsQuantity;
          let findIndex = lineArray.indexOf(childItem.lineName);
          if (findIndex === -1) {
            lineArray.push(childItem.lineName);
            if (lineArray.length > maxLengthOfLineArray) {
              maxLengthOfLineArray = lineArray.length;
            }
          }
          //child line get outside
          item[`lineName_${index}`] = childItem.lineName;
          item[`lineName_${index}_qty`] =
            childItem.allocatedProducedMaterialsQuantity;

          if (areColumnsEditable && childItem.recommendedProductionDate) {
            item[`lineName_${index}_qty_isEditable`] = true;
          }
          if (areColumnsEditable && !childItem.recommendedProductionDate) {
            item[`lineName_${index}_qty_isEditable`] = false;
          }
        },
      );
      item['unAllocatedQty'] =
        item.totalProducedChildMaterialsQuantity - sumOfChildQty;
      // to create line columns
      if (indexValue === allocatedChildProducedMaterials.length - 1) {
        let lineColumns = [];
        for (let index = 0; index < maxLengthOfLineArray; index++) {
          lineColumns.push(
            {
              width: 70,
              title: 'Line',
              field: `lineName_${index}`,
              enableSearch: false,
              enableFilter: false,
            },
            {
              width: 100,
              title: 'Quantity',
              field: `lineName_${index}_qty`,
              enableSearch: false,
              enableFilter: false,
              render: (row, i) => {
                if (
                  row[`lineName_${index}_qty_isEditable`] &&
                  row[`lineName_${index}`]
                )
                  return (
                    <input
                      className={
                        row.unAllocatedQty < 0
                          ? clsx(styles.inputBox, styles.errorInput)
                          : styles.inputBox
                      }
                      onChange={e =>
                        handleEdit(e.target.value, `lineName_${index}_qty`, i)
                      }
                      onKeyPress={e => {
                        if (e?.key === '-' || e?.key === '+') {
                          e.preventDefault();
                        }
                      }}
                      defaultValue={row[`lineName_${index}_qty`]}
                      aria-label={`plan-input-for-${index}`}
                    />
                  );
                return <div>{row[`lineName_${index}_qty`]}</div>;
              },
            },
          );
        }
        setLineColumns(lineColumns);
      }
    });
    setRowsData({
      loading: false,
      data: allocatedChildProducedMaterials,
      filteredData: [...allocatedChildProducedMaterials],
    });
  };

  const editRows = () => {
    setEditableColumns(true);
    setHasActiveEdits(true);
  };

  const resolveConflict = async () => {
    let updateData = [];
    rowsData.filteredData.forEach((element, i) => {
      let childMaterial = [];

      for (
        var childProduceMaterialsIndex = 0;
        childProduceMaterialsIndex <
        element.lineAndAllocatedProducedChildMaterialsQuantities.length;
        childProduceMaterialsIndex++
      ) {
        childMaterial.push({
          lineId:
            element.lineAndAllocatedProducedChildMaterialsQuantities[
              childProduceMaterialsIndex
            ].lineId,
          quantity: parseInt(
            element[`lineName_${childProduceMaterialsIndex}_qty`] === ''
              ? 0
              : element[`lineName_${childProduceMaterialsIndex}_qty`],
          ),
        });
      }

      updateData.push({
        parentLineId: element.parentLineId,
        childMaterialNumber: element.childMaterialNumber,
        rootMaterial: element.rootMaterial,
        lineAndAllocatedMaterialQuantities: childMaterial,
      });
    });

    try {
      showLoading();
      let response;
      if (plant.label === PANTNAGAR_PLANT_NAME && materialGroupCluster === 'BIW') {
        const payload = updateData.map(item => ({
          parentLineId: item.parentLineId,
          childMaterialNumber: item.childMaterialNumber,
          rootMaterial: item.rootMaterial,
          lineAndAllocatedMaterialQuantities: item.lineAndAllocatedMaterialQuantities,
        }));
        response = await API.patch(
          API_RESOURCE_URLS.resolveAggregatePlanConflicts(
            plant.value,
            activeAsnDate.format(DATE_FORMAT.ISO),
          ),
          payload,
        );
      } else {
        const payload = updateData.map(({ rootMaterial, ...rest }) => rest);
        response = await API.patch(
          API_RESOURCE_URLS.resolveAggregatePlanConflicts(
            plant.value,
            activeAsnDate.format(DATE_FORMAT.ISO),
          ),
          payload,
        );
      }
      if (response) {
        await loadAggregateConflicts(); // refresh data
        closePopUp();
        showPopup({
          type: MESSAGE_TYPE.SUCCESS,
          contextText: DISPLAY_MESSAGES.RESOLVE_CONFLICT_SUCCESS,
        });
        setEditableColumns(false);
        setValidData(false);
        setHasActiveEdits(false);
        setIsRenderEditQty(false);
      }
    } catch (error) {
      console.error('Resolve conflict failed.' + buildErrorMessage(error));
      showInternalError({
        contextText: DISPLAY_MESSAGES.RESOLVE_CONFLICT_FAILED,
        error,
      });
    }
  };

  const handleEdit = (e, field, i) => {
    setEndEditing({ isDataEdited: true, updatedData: [e, field, i] });
    setIsRenderEditQty(true);
  };
  const removeDuplicates = (arr) => {
    const jsonObject = arr.map(JSON.stringify);
    const uniqueSet = new Set(jsonObject);
    return Array.from(uniqueSet).map(JSON.parse);
  }

  const [finalLinesConflictData, setFinalLinesConflictData] = useState([]);
  const setGrandTotalCount = filteredDataArray => {
    const noOfAggregateLinesAndQuantity = filteredDataArray.map(rowItem => rowItem.lineAndAllocatedProducedChildMaterialsQuantities.map(
      (lineAndAllocatedQty, asnLineIndex) => {
        const filterData = filteredDataArray?.map(lineAndItsQuantity => {
          if (
            lineAndItsQuantity[`lineName_${asnLineIndex}`] ===
            lineAndAllocatedQty.lineName
          ) {
            return {
              lineName: lineAndItsQuantity[`lineName_${asnLineIndex}`],
              conflictCount:
                lineAndItsQuantity[`lineName_${asnLineIndex}_qty`] === ''
                  ? 0
                  : parseInt(lineAndItsQuantity[`lineName_${asnLineIndex}_qty`]),
            };
          }
        });
        return filterData.filter(item => item !== undefined);
      },
    ));
    const uniqueData = []
    const reduceData = noOfAggregateLinesAndQuantity.map(rowData => rowData.map(childData => uniqueData.push(childData)))

    const makeOneDataObj = []
    const data = removeDuplicates(uniqueData).map(itemsData => itemsData.map(item => makeOneDataObj.push(item)))

    const groupByCategory = (items) => {
      return items.reduce((acc, item) => {
        const { lineName } = item;
        if (!acc[lineName]) {
          acc[lineName] = [];
        }
        acc[lineName].push(item);
        return acc;
      }, {});
    }

    const deSerializeData = [];
    [groupByCategory(makeOneDataObj)].forEach(obj => {
      // Use Object.entries() to get an array of [key, value] pairs
      Object.entries(obj).forEach(([key, value]) => {
        deSerializeData.push(value)
      });
    });
    const aggregateLineWiseCount = deSerializeData?.map(lineWiseConflictList =>
      lineWiseConflictList?.reduce(
        (previousCount, lineCount) =>
          parseInt(previousCount) + parseInt(lineCount.conflictCount),
        0,
      ),
    );
    setFinalLinesConflictData(aggregateLineWiseCount);
  };

  const updateConflictEditedData = (dataToBeUpdated, isDataEdited, editedData) => {
    if (isDataEdited) {
      const [value, fieldName, selectedIndex] = editedData;
      let parentIndex = selectedIndex - 1;
      let totalAllocatedQty = 0;
      let filteredDataArray = dataToBeUpdated;
      for (var key in filteredDataArray[parentIndex]) {
        if (key === fieldName)
          filteredDataArray[parentIndex][fieldName] = value;
        for (
          var childProducedMaterialsIndex = 0;
          childProducedMaterialsIndex <
          filteredDataArray[parentIndex]
            .lineAndAllocatedProducedChildMaterialsQuantities.length;
          childProducedMaterialsIndex++
        ) {
          //calculate total allocated qty for each row
          if (key === `lineName_${childProducedMaterialsIndex}_qty`) {
            const enteredValue = filteredDataArray[parentIndex][key]
              ? parseInt(filteredDataArray[parentIndex][key])
              : 0;
            totalAllocatedQty += enteredValue;
          }
        }
      }
      //set updated unAllocated qty
      filteredDataArray[parentIndex].unAllocatedQty =
        filteredDataArray[parentIndex].totalProducedChildMaterialsQuantity -
        totalAllocatedQty;

      setRowsData(prevState => ({
        ...prevState,
        filteredData: filteredDataArray,
      }));
      //check data is valid or not
      let checkUnallocatedData = rowsData.filteredData.filter(
        item => item.unAllocatedQty < 0 || item.unAllocatedQty !== 0,
      );
      setValidData(checkUnallocatedData.length > 0 ? false : true);
      setEndEditing({ isDataEdited: false, updatedData: [] });
    }
  };

  const redirectToApplicationLandingPage = () => {
    history.push('/');
  };

  const prepareJsonHeaderAndValueFormats = (aggregatePartNames, lineColumns) => {
    const vcAggregateHeaderAndValueFormats = aggregatePartNames.map(
      partName => ({
        label: partName + ' Status',
        value: row =>
          row.materialGroupClusters.filter(group => group.name === partName)[0]
            ?.materials[0]?.aggregateStatus || 'unavailable',
      }),
    );

    const vcHeaderAndValueFormats = [
      {
        label: 'Part Number',
        value: row => '="' + row.childMaterialNumber + '"',
      },
      {
        label: 'Part Description',
        value: row => row.childMaterialDescription,
      },
      {
        label: 'VC Alias',
        value: row => row.rootMaterialAlias || '',
      },
      {
        label: 'Chassis Line',
        value: row => row.parentLineName,
      },
      {
        label: 'Total Quantity',
        value: row => row.totalProducedChildMaterialsQuantity,
      },
      {
        label: 'Conflicted Quantity',
        value: row => row.unAllocatedQty,
      },
    ].filter(c => c);
    return [...vcHeaderAndValueFormats, ...vcAggregateHeaderAndValueFormats];
  };

  const generateAndDownloadLineoutCsvReport = filteredData => {
    try {
      if (!rowsData.loading) {
        const dateValue = moment().format(DATE_FORMAT.ISO);

        const jsonData = filteredData;

        const aggregatePartNames = [];

        const dynamicLineColumns = [];

        for (let index = 0; index < lineColumns.length; index++) {
          if (index % 2 === 0) {
            dynamicLineColumns.push({
              label: 'Line',
              value: data => data[`lineName_${index / 2}`],
            });
          } else {
            dynamicLineColumns.push({
              label: 'Quantity',
              value: data => data[`lineName_${(index - 1) / 2}_qty`],
            });
          }
        }

        const { parseAsync } = require('json2csv');
        const fields = prepareJsonHeaderAndValueFormats(aggregatePartNames).concat(dynamicLineColumns);
        const opts = { fields };
        parseAsync(jsonData, opts)
          .then(csv => {
            const exportedFilename =
              'AggregateConflictReport_' +
              plant.label +
              '_' +
              line.label +
              '_' +
              dateValue +
              '.csv';
            downloadCsv(csv, exportedFilename);
          })
          .catch(error =>
            console.error(
              'Failed to download Aggregate Conflict report, error:' +
              buildErrorMessage(error),
            ),
          );
      }
    } catch (error) {
      console.error(
        'Failed to download Aggregate Conflict report, error:' + buildErrorMessage(error),
      );
    }
  };

  useEffect(() => {
    setHasActiveEdits(areColumnsEditable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areColumnsEditable]);

  useEffect(() => {
    if (updatedValue.updatedData.length > 0) {
      updateConflictEditedData(rowsData?.filteredData, updatedValue.isDataEdited, updatedValue.updatedData);
      setGrandTotalCount(rowsData?.filteredData);
    }
    if (isRenderEditQty === false) setFinalLinesConflictData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsData.filteredData, updatedValue.isDataEdited, updatedValue.updatedData]);

  useEffect(() => {
    fetchAggregateConflictData(rowsData.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRefreshedDate, rowsData.data]);

  useEffect(() => {
    setEditableColumns(false);
    setHasActiveEdits(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={styles.backButton}>
        <BackButton
          basePath={materialGroupCluster}
          action="Plan"
          handleClick={redirectToApplicationLandingPage}
        />
      </div>
      <AggregateConflictToolbar
        lastRefreshedDate={lastRefreshedDate}
        startDate={startDate}
        activeAsnDate={activeAsnDate}
        handleDateSelect={date => setActiveAsnDate(date)}
        hasActiveEdits={hasActiveEdits}
        restrictNavigationDuringEditing
      />
      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: '100%' }}>
          <AggregateConflictTable
            columns={[...aggregateColumns, ...optionalColumns, ...lineColumns]}
            defaultColumns={
              plant.label === PANTNAGAR_PLANT_NAME && materialGroupCluster === 'BIW'
                ? [...aggregateColumns, ...optionalColumns, ...lineColumns]
                : [...aggregateColumns, ...lineColumns]
            }
            rowHeight={ROW_HEIGHT}
            rows={rowsData.data}
            editConflictCount={finalLinesConflictData}
            isRenderEditQtyStatus={isRenderEditQty}
            primaryAction={{
              name: 'Resolve Conflict',
              actionFn: resolveConflict,
              authOperation: USER_OPERATIONS.ASSIGN_MATERIAL_TO_LINE,
              enableButton: isDataValid,
            }}
            secondaryActions={[
              {
                name: 'Edit',
                actionFn: editRows,
                backgroundColor: '#59a385',
                enableButton:
                  !areColumnsEditable &&
                  rowsData.data.length > 0 &&
                  rowsData?.data[0]?.lineAndAllocatedProducedChildMaterialsQuantities
                    .map(item => item.recommendedProductionDate)
                    .some(item => item !== null),
                authOperation: USER_OPERATIONS.ASSIGN_MATERIAL_TO_LINE,
              },
              {
                name: 'DOWNLOAD CSV',
                getFilteredData: true,
                authOperation: USER_OPERATIONS.GET_CHASSIS_PLAN_ASN,
                shouldEnable: () => rowsData.data.length > 0,
                actionFn: filteredData =>
                  generateAndDownloadLineoutCsvReport(filteredData),
              },
            ]}
            setHasActiveEdits={() => { }}
            setAsnCount={setConflictCount}
            hideCheckbox={true}
            isDataLoading={rowsData.loading}
          />
        </div>
      </div>
    </>
  );
};

AggregateConflict.propTypes = {
  aggregate: PropTypes.string.isRequired,
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
  loadAggregateConflicts: PropTypes.func.isRequired,
  lastRefreshedDate: PropTypes.objectOf(moment).isRequired,
  setConflictCount: PropTypes.func.isRequired,
  rowsData: PropTypes.arrayOf(
    PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.array.isRequired,
      filteredData: PropTypes.array.isRequired,
    }),
  ),
  setRowsData: PropTypes.func.isRequired,
  startDate: PropTypes.objectOf(moment).isRequired,
  activeAsnDate: PropTypes.objectOf(moment).isRequired,
  setActiveAsnDate: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
  AggregateConflict,
  true,
  RESOURCE_TYPE.MATERIAL,
);